import React, { useCallback, useState } from 'react';
import { saveAs } from 'file-saver';

import CursorListBody from '../shared/cursor_list_body';
import StatusPill from '../shared/status_pill';
import useCursorList from '../shared/use_cursor_list';
import { fetch_invoice_pdf, fetch_invoices_list, fetch_l1 } from '../api';
import {
  VIEWS,
  VIEW_AWAITING,
  awaiting_view,
  invoice_error,
  invoice_pdf_name,
  list_view
} from './adapters';
import { STATUS_CONFIRMED, awaiting_l1_params } from './requests';
import '../payouts/payouts.css';

/*
 * PROD-7828 W3 — the Invoices tab (/teacher-payments/invoices): the month's confirmed and replaced
 * invoices with their PDF, and the closed teacher-months still awaiting the teacher's invoice.
 * Each PDF download is audited server-side (it carries PAN and GSTIN). A 403 is shown in the tab.
 * props: pay_month, month_label, on_error(e, fallback) (401 only), on_notify(type, message).
 */
const LIST_FAILED_TEXT = 'Could not load the invoices.';
const AWAITING_FAILED_TEXT = 'Could not load the teachers awaiting an invoice.';
const PDF_FAILED_TEXT = 'Could not download the invoice PDF. Try again.';

const invoice_columns = (on_pdf, busy_uuid) => [
  { key: 'teacher_name', label: 'Teacher', hideInCard: true },
  {
    key: 'invoice_number',
    label: 'Invoice',
    render: (r) => <span className="tp-mono">{r.invoice_number}</span>
  },
  { key: 'version', label: 'Close version' },
  {
    key: 'status',
    label: 'Status',
    render: (r) => (
      <div>
        <StatusPill tone={r.status.tone}>{r.status.label}</StatusPill>
        {r.superseded_at ? <div className="tp-hint">Replaced {r.superseded_at} IST</div> : null}
      </div>
    )
  },
  { key: 'taxable', label: 'Taxable', align: 'right' },
  { key: 'gst', label: 'GST', align: 'right' },
  { key: 'invoice_total', label: 'Invoice total', align: 'right' },
  { key: 'tds', label: 'TDS', align: 'right' },
  { key: 'payable', label: 'We pay', align: 'right' },
  { key: 'confirmed_at', label: 'Confirmed (IST)' },
  {
    key: 'pdf',
    label: 'PDF',
    hideInCard: true,
    render: (r) => {
      const busy = busy_uuid === r.invoice_uuid;
      return (
        <button
          type="button"
          className="tp-link"
          aria-label={`Download invoice ${r.invoice_number} PDF`}
          onClick={busy ? undefined : () => on_pdf(r)}
          aria-disabled={busy ? 'true' : undefined}
        >
          {busy ? 'Downloading…' : 'Download'}
        </button>
      );
    }
  }
];

const awaiting_columns = [
  {
    key: 'teacher',
    label: 'Teacher',
    hideInCard: true,
    render: (r) => (
      <div>
        <div>{r.teacher_name}</div>
        {r.teacher_email ? <div className="tp-hint">{r.teacher_email}</div> : null}
      </div>
    )
  },
  { key: 'payable', label: 'Payable', align: 'right' },
  {
    key: 'note',
    label: 'Note',
    render: (r) =>
      r.reopened ? (
        <span className="tp-hint">
          Reopened and closed again: the teacher confirms the new invoice.
        </span>
      ) : (
        <span className="tp-hint">Not confirmed yet</span>
      )
  }
];

const InvoicesTab = ({ pay_month, month_label, on_error, on_notify }) => {
  const [view, setView] = useState(STATUS_CONFIRMED);
  const [busy_uuid, setBusyUuid] = useState('');
  const awaiting = view === VIEW_AWAITING;

  const read = useCallback(
    (cursor) =>
      awaiting
        ? fetch_l1(awaiting_l1_params(pay_month, cursor || 0)).then((d) => {
            const v = awaiting_view(d, cursor || 0);
            return { rows: v.rows, next: v.next_offset };
          })
        : fetch_invoices_list(pay_month, view, cursor).then((d) => {
            const v = list_view(d);
            return { rows: v.rows, next: v.next_after_id };
          }),
    [pay_month, view, awaiting]
  );
  const list = useCursorList(
    read,
    awaiting ? AWAITING_FAILED_TEXT : LIST_FAILED_TEXT,
    on_error,
    invoice_error
  );

  const download_pdf = (row) => {
    setBusyUuid(row.invoice_uuid);
    fetch_invoice_pdf(row.invoice_uuid)
      .then(({ blob, file_name }) => {
        setBusyUuid('');
        saveAs(blob, invoice_pdf_name(row.invoice_number, file_name));
      })
      .catch((e) => {
        setBusyUuid('');
        const v = invoice_error(e, PDF_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, PDF_FAILED_TEXT);
        else on_notify('error', v.text);
      });
  };

  return (
    <div className="tp-inv-tab">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" role="heading" aria-level={2}>
          Invoices
        </span>
        <span className="tp-card-hd-sub">{month_label}</span>
      </div>
      <div className="tp-hint tp-po-intro">
        Teachers confirm their invoice in the teacher app after the month is closed. A month that is
        reopened and closed again needs a new confirmation; the earlier invoice shows as replaced.
      </div>

      <div className="tp-seg" role="group" aria-label="Invoices list">
        {VIEWS.map((v) => (
          <button
            key={v.value}
            type="button"
            className={view === v.value ? 'on' : ''}
            aria-pressed={view === v.value}
            onClick={() => setView(v.value)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {awaiting ? (
        <CursorListBody
          list={list}
          columns={awaiting_columns}
          rowKey={(r) => r.teacher_uuid}
          empty_text="No closed teacher-month is waiting for an invoice."
          title_of={(r) => r.teacher_name}
        />
      ) : (
        <CursorListBody
          list={list}
          columns={invoice_columns(download_pdf, busy_uuid)}
          rowKey={(r) => r.invoice_uuid}
          empty_text="No invoices for this month and filter."
          title_of={(r) => `${r.teacher_name} · ${r.invoice_number}`}
        />
      )}
    </div>
  );
};

export default InvoicesTab;
